import React, { useEffect, useState } from "react";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import { api, fmtUSD, fmtNum } from "../lib/api";
import { useYear } from "../context/YearContext";
import { Map, TrendingDown, ChevronRight, ChevronDown } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell,
} from "recharts";

export default function FieldPnL() {
  const { year } = useYear();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get(`/reports/field-pnl?year=${year}`).then((r) => setData(r.data));
  }, [year]);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  if (!data) {
    return (
      <div>
        <TopBar title="Field P&L" subtitle="Loading…" />
      </div>
    );
  }

  const { fields, unallocated, totals } = data;

  return (
    <div>
      <TopBar
        title="Field-by-Field P&L"
        subtitle={`Which fields are making you money in ${year}? Tag yields and expenses to a field to see them broken out below.`}
      />

      {/* Totals */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-[#1F1E1B] p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Total Revenue</div>
          <div className="font-display text-3xl font-black tabular">{fmtUSD(totals.revenue)}</div>
        </div>
        <div className="bg-white border border-[#1F1E1B] p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Allocated Expenses</div>
          <div className="font-display text-3xl font-black tabular">{fmtUSD(totals.expenses)}</div>
        </div>
        <div className={`p-4 border ${totals.profit >= 0 ? "bg-[#1A4135] border-[#1A4135] text-white" : "bg-[#BC4727] border-[#BC4727] text-white"}`}>
          <div className={`text-[10px] font-bold uppercase tracking-[0.18em] ${totals.profit >= 0 ? "text-[#C8D5CE]" : "text-[#F8D5C8]"}`}>
            Combined P&L
          </div>
          <div className="font-display text-3xl font-black tabular">{fmtUSD(totals.profit)}</div>
        </div>
      </div>

      {fields.length === 0 ? (
        <EmptyState
          icon={Map}
          title="No fields to compare yet"
          hint={`Add fields and tag yields/expenses to them so you can see profit per field for ${year}.`}
          testid="empty-pnl"
        />
      ) : (
        <>
          {/* Comparison bar chart */}
          <div className="bg-white border border-[#1F1E1B] p-6 mb-6" data-testid="pnl-chart">
            <div className="mb-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Comparison</div>
              <h3 className="font-display text-xl font-bold">Revenue vs Expenses by Field</h3>
            </div>
            <ResponsiveContainer width="100%" height={Math.max(280, fields.length * 50)}>
              <BarChart
                data={fields}
                layout="vertical"
                margin={{ top: 10, right: 30, left: 80, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="2 2" stroke="#E5E2D6" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#66645E" }} tickFormatter={(v) => `$${v}`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#1F1E1B", fontWeight: 600 }} width={120} />
                <Tooltip
                  formatter={(v) => fmtUSD(v)}
                  contentStyle={{ border: "1px solid #1F1E1B", borderRadius: 0, background: "#fff" }}
                />
                <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="revenue" name="Revenue" fill="#1A4135" />
                <Bar dataKey="expenses" name="Expenses" fill="#BC4727" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Profit per acre chart */}
          <div className="bg-white border border-[#1F1E1B] p-6 mb-6" data-testid="pnl-per-acre">
            <div className="mb-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Efficiency</div>
              <h3 className="font-display text-xl font-bold">Profit per Acre</h3>
            </div>
            <ResponsiveContainer width="100%" height={Math.max(220, fields.length * 40)}>
              <BarChart data={fields} layout="vertical" margin={{ top: 10, right: 30, left: 80, bottom: 10 }}>
                <CartesianGrid strokeDasharray="2 2" stroke="#E5E2D6" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#66645E" }} tickFormatter={(v) => `$${v}`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#1F1E1B", fontWeight: 600 }} width={120} />
                <Tooltip
                  formatter={(v) => fmtUSD(v)}
                  contentStyle={{ border: "1px solid #1F1E1B", borderRadius: 0 }}
                />
                <Bar dataKey="profit_per_acre" name="$/Acre">
                  {fields.map((r, i) => (
                    <Cell key={i} fill={r.profit_per_acre >= 0 ? "#1A4135" : "#BC4727"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Detail table */}
          <div className="bg-white border border-[#1F1E1B] overflow-x-auto" data-testid="pnl-table">
            <table className="ledger-table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Crop</th>
                  <th className="num">Acres</th>
                  <th className="num">Revenue</th>
                  <th className="num">Expenses</th>
                  <th className="num">Profit / (Loss)</th>
                  <th className="num">$/Acre</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((r) => {
                  const hasPlots = (r.plots || []).length > 0;
                  const open = expanded[r.field_id];
                  return (
                  <React.Fragment key={r.field_id}>
                  <tr data-testid={`pnl-row-${r.field_id}`}>
                    <td className="font-semibold">
                      {hasPlots ? (
                        <button
                          onClick={() => toggle(r.field_id)}
                          data-testid={`pnl-toggle-${r.field_id}`}
                          className="inline-flex items-center gap-1.5 hover:underline"
                        >
                          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          {r.name}
                          <span className="text-[9px] uppercase tracking-wider font-bold text-[#BC4727] ml-1">
                            {r.plots.length} plot{r.plots.length === 1 ? "" : "s"}
                          </span>
                        </button>
                      ) : r.name}
                    </td>
                    <td><span className="stamp">{r.crop_type}</span></td>
                    <td className="num">{fmtNum(r.acreage, 2)}</td>
                    <td className="num">{fmtUSD(r.revenue)}</td>
                    <td className="num">{fmtUSD(r.expenses)}</td>
                    <td className={`num font-bold ${r.profit >= 0 ? "text-[#1A4135]" : "text-[#BC4727]"}`}>
                      {fmtUSD(r.profit)}
                    </td>
                    <td className={`num font-bold ${r.profit_per_acre >= 0 ? "text-[#1A4135]" : "text-[#BC4727]"}`}>
                      {fmtUSD(r.profit_per_acre)}
                    </td>
                  </tr>
                  {hasPlots && open && r.plots.map((p) => (
                    <tr key={`${r.field_id}-${p.plot_id || "unalloc"}`}
                        data-testid={`pnl-plot-row-${r.field_id}-${p.plot_id || "unalloc"}`}
                        className="bg-[#FAF7EC]/60">
                      <td className="pl-10 text-[#66645E] italic text-sm">
                        <span className="text-[#BC4727] font-bold mr-1">↳</span>
                        {p.is_unallocated ? "Not tagged to a plot" : `${p.crop}`}
                      </td>
                      <td><span className={`stamp ${p.is_unallocated ? "" : "stamp-green"}`}>{p.is_unallocated ? "—" : p.crop}</span></td>
                      <td className="num text-[#66645E]">{p.acres ? fmtNum(p.acres, 2) : "—"}</td>
                      <td className="num text-[#66645E]">{fmtUSD(p.revenue)}</td>
                      <td className="num text-[#66645E]">{fmtUSD(p.expenses)}</td>
                      <td className={`num font-bold ${p.profit >= 0 ? "text-[#1A4135]" : "text-[#BC4727]"}`}>
                        {fmtUSD(p.profit)}
                      </td>
                      <td className={`num font-bold ${p.profit_per_acre >= 0 ? "text-[#1A4135]" : "text-[#BC4727]"}`}>
                        {p.acres ? fmtUSD(p.profit_per_acre) : "—"}
                      </td>
                    </tr>
                  ))}
                  </React.Fragment>
                  );
                })}
                {(unallocated.revenue > 0 || unallocated.expenses > 0) && (
                  <tr className="bg-[#FAF7EC]" data-testid="pnl-unallocated">
                    <td className="italic">Unallocated</td>
                    <td className="text-[#66645E] text-xs italic">Not tagged to any field</td>
                    <td className="num">—</td>
                    <td className="num">{fmtUSD(unallocated.revenue)}</td>
                    <td className="num">{fmtUSD(unallocated.expenses)}</td>
                    <td className={`num font-bold ${unallocated.profit >= 0 ? "text-[#1A4135]" : "text-[#BC4727]"}`}>
                      {fmtUSD(unallocated.profit)}
                    </td>
                    <td className="num">—</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {unallocated.expenses > 0 && (
            <div className="mt-3 text-xs text-[#66645E] flex items-start gap-2">
              <TrendingDown className="w-3 h-3 mt-0.5" />
              <span>
                Tip: tag general expenses (insurance, lease, fuel) to a specific field on the Expenses page to see them counted in that field's P&L.
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
